import { zipSync } from "fflate";
import { Uint8ArrayReader, Uint8ArrayWriter, ZipWriter } from "@zip.js/zip.js";
import { csvCell } from "@/lib/csv";
import { containsProductKey } from "@/lib/product-key-redaction";
import type { Entitlement, Policy } from "@/lib/types";

const encoder = new TextEncoder();
const ZIP_DATE = new Date("2000-01-01T00:00:00.000Z");
const PDF_NAME = "license-certificate.pdf";
const CSV_NAME = "product-keys.csv";
const MANIFEST_NAME = "manifest.json";

export type DeliveryPackLanguage = "en";

export interface DeliveryPackInput {
  batch: { id: string; issuedAt: string };
  customer: { id: string; name: string };
  product: { id: string; name: string; sku?: string };
  license: { id: string; model?: string; kind?: string; status?: string; validFrom?: string; validUntil?: string };
  policy: Policy;
  entitlements: Record<string, Entitlement>;
  productKeys: ReadonlyArray<{ id: string; key: string; maskedSuffix?: string }>;
  pdfBytes: Uint8Array;
  language: DeliveryPackLanguage;
  generatedAt: string;
  orderReference?: string;
  recipientLabel?: string;
}

export interface DeliveryPackFile {
  name: typeof PDF_NAME | typeof CSV_NAME | typeof MANIFEST_NAME;
  bytes: Uint8Array;
  sha256: string;
}

export interface DeliveryPack {
  zipBytes: Uint8Array;
  zipFilename: string;
  zipSha256: string;
  files: readonly DeliveryPackFile[];
  audit: {
    artifactFormat: "delivery-pack-v1";
    language: DeliveryPackLanguage;
    fileCount: 3;
    byteLength: number;
    clientReportedZipSha256: string;
  };
}

export interface DeliveryPackBuildOptions {
  password?: string;
  signal?: AbortSignal;
}

function sanitizeText(value: string | undefined) {
  return (value ?? "").replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim();
}

export function isValidDeliveryPackPassword(password: string) {
  return password.length >= 1 && password.length <= 256 && !/[^\x20-\x7E]/.test(password);
}

async function zipEncrypted(files: readonly DeliveryPackFile[], password: string, signal?: AbortSignal) {
  if (!isValidDeliveryPackPassword(password)) throw new Error("Delivery Pack password must be 1 to 256 printable ASCII characters");
  const writer = new ZipWriter(new Uint8ArrayWriter(), {
    password,
    zipCrypto: true,
    signal,
    lastModDate: ZIP_DATE,
    extendedTimestamp: false,
  });
  for (const file of files) {
    await writer.add(file.name, new Uint8ArrayReader(file.bytes), { lastModDate: ZIP_DATE, extendedTimestamp: false });
  }
  return writer.close();
}

function normalizeProductKeyText(value: string | undefined) {
  return sanitizeText(value).normalize("NFKC").toUpperCase().replace(/[- ]/g, "");
}

// Inspect JSON property names as well as nested values before adding metadata.
function metadataStrings(value: unknown): string[] {
  if (value === null || value === undefined) return [];
  if (typeof value !== "object") return [String(value)];
  return Object.entries(value).flatMap(([key, item]) => [key, ...metadataStrings(item)]);
}

export function assertSecretsOnlyInCSV(input: DeliveryPackInput) {
  const metadata = [
    input.batch.id,
    input.batch.issuedAt,
    input.customer.id,
    input.customer.name,
    input.product.id,
    input.product.name,
    input.product.sku,
    input.license.id,
    input.license.model,
    input.license.kind,
    input.license.status,
    input.license.validFrom,
    input.license.validUntil,
    input.generatedAt,
    input.orderReference,
    input.recipientLabel,
    ...metadataStrings(input.policy),
    ...metadataStrings(input.entitlements),
    ...input.productKeys.flatMap((item) => [item.id, item.maskedSuffix]),
  ];
  if (metadata.some((value) => containsProductKey((value ?? "").normalize("NFKC")))) {
    throw new Error("Delivery Pack metadata must not contain a full Product Key");
  }
  const normalizedMetadata = metadata.map(normalizeProductKeyText);

  for (const item of input.productKeys) {
    const secret = normalizeProductKeyText(item.key);
    if (secret && normalizedMetadata.some((value) => value.includes(secret))) {
      throw new Error("Delivery Pack metadata must not contain a full Product Key");
    }
  }
}

function batchFilename(batchID: string) {
  const cleaned = batchID.normalize("NFKC").replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
  return `${cleaned || "batch"}.zip`;
}

async function sha256(bytes: Uint8Array) {
  // Copy into an ArrayBuffer-backed view. TypeScript 5.9 distinguishes this
  // from a view backed by SharedArrayBuffer, which Web Crypto does not accept.
  const digest = await crypto.subtle.digest("SHA-256", new Uint8Array(bytes));
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function csv(input: DeliveryPackInput) {
  const header = ["product_key_id", "product_key", "batch_id", "license_id", "product_id"];
  const rows = input.productKeys.map((item) => [item.id, item.key, input.batch.id, input.license.id, input.product.id]);
  return `${[header, ...rows].map((row) => row.map(csvCell).join(",")).join("\r\n")}\r\n`;
}

/** Creates an in-memory, deterministic FR-KEY-005 Delivery Pack. */
export async function buildDeliveryPack(input: DeliveryPackInput, options: DeliveryPackBuildOptions = {}): Promise<DeliveryPack> {
  if (!input.productKeys.length) throw new Error("Delivery Pack requires at least one Product Key");
  if (!input.pdfBytes.byteLength) throw new Error("Delivery Pack requires PDF bytes");
  // Keep the checked metadata stable while asynchronous hashing yields control.
  const policy = structuredClone(input.policy);
  const entitlements = structuredClone(input.entitlements);
  assertSecretsOnlyInCSV({ ...input, policy, entitlements });
  const pdf = input.pdfBytes;
  const csvBytes = encoder.encode(csv(input));
  const manifestBase = {
    version: 1,
    batch_id: input.batch.id,
    issued_at: input.batch.issuedAt,
    generated_at: input.generatedAt,
    license_id: input.license.id,
    product_id: input.product.id,
    customer_id: input.customer.id,
    language: input.language,
    policy,
    entitlements,
    order_reference: input.orderReference ? sanitizeText(input.orderReference) : undefined,
    recipient_label: input.recipientLabel ? sanitizeText(input.recipientLabel) : undefined,
    files: [
      { name: PDF_NAME, bytes: pdf.byteLength, sha256: await sha256(pdf) },
      { name: CSV_NAME, bytes: csvBytes.byteLength, sha256: await sha256(csvBytes) },
    ],
  };
  const manifest = encoder.encode(`${JSON.stringify(manifestBase, null, 2)}\n`);
  const files: DeliveryPackFile[] = [
    { name: PDF_NAME, bytes: pdf, sha256: await sha256(pdf) },
    { name: CSV_NAME, bytes: csvBytes, sha256: await sha256(csvBytes) },
    { name: MANIFEST_NAME, bytes: manifest, sha256: await sha256(manifest) },
  ];
  const zipBytes = options.password === undefined
    ? zipSync(Object.fromEntries(files.map((file) => [file.name, [file.bytes, { mtime: ZIP_DATE, level: 9 }]])), { mtime: ZIP_DATE, level: 9 })
    : await zipEncrypted(files, options.password, options.signal);
  const zipSha256 = await sha256(zipBytes);
  return {
    zipBytes,
    zipFilename: batchFilename(input.batch.id),
    zipSha256,
    files,
    audit: {
      artifactFormat: "delivery-pack-v1",
      language: input.language,
      fileCount: 3,
      byteLength: zipBytes.byteLength,
      clientReportedZipSha256: zipSha256,
    },
  };
}

export const deliveryPackFiles = { pdf: PDF_NAME, csv: CSV_NAME, manifest: MANIFEST_NAME } as const;
